import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

interface Signal {
  id: number;
  kind: 'funding' | 'job_change' | 'tdnet' | 'hiring_spike' | 'tech_install';
  title: string;
  subtitle: string;
  score: number;
  occurredAt: string;
  ai: string;
}

const SIGNALS: Signal[] = [
  {
    id: 1,
    kind: 'funding',
    title: 'Funding · ¥1.2B Series B — TechRise Inc.',
    subtitle: 'Lead investor: SoftBank · 2 contacts at TechRise',
    score: 92,
    occurredAt: '2026-05-22T03:14:00Z',
    ai: 'TechRise just closed ¥1.2B Series B. Two of your contacts (CTO Sato-san, VP Eng Hashimoto-san) are likely involved in tooling decisions. Suggest sending an introductory note in 紹介経由 register citing the round, with a discovery slot in week 2.',
  },
  {
    id: 2,
    kind: 'job_change',
    title: 'Job change · Tanaka-san → VP Sales at Meishi K.K.',
    subtitle: 'Was: SDR Manager · 1 mutual contact',
    score: 78,
    occurredAt: '2026-05-21T08:42:00Z',
    ai: 'Tanaka-san just moved into a VP Sales role at Meishi K.K. — historically a champion buyer for our category. Congratulate (in 友好的 register) and offer a 15-minute briefing on the wedge that won her over at her previous company.',
  },
  {
    id: 3,
    kind: 'tdnet',
    title: 'TDNet 適時開示 · 業績予想の修正(上方修正)— Yamato Shoji',
    subtitle: 'Upward revision · last touchpoint: 14d ago',
    score: 74,
    occurredAt: '2026-05-21T15:01:00Z',
    ai: 'Yamato Shoji issued an upward earnings revision today. Existing deal in negotiation stage — the fiscal cover this news provides may unstick the budget conversation. Suggest re-engaging the CFO contact in 二回目以降 register.',
  },
  {
    id: 4,
    kind: 'hiring_spike',
    title: 'Hiring · 8 SecOps roles opened — Acme K.K.',
    subtitle: 'New: Security Architect, SRE Lead, Detection Eng × 4',
    score: 68,
    occurredAt: '2026-05-20T11:30:00Z',
    ai: 'Acme is hiring aggressively for security — likely a SecOps program initiative. Map your security-product narrative against the role specs; the Detection Eng JD references the exact tooling we replace.',
  },
];

const KIND_COLOR: Record<Signal['kind'], string> = {
  funding: 'bg-emerald-50 text-emerald-700',
  job_change: 'bg-amber-50 text-amber-700',
  tdnet: 'bg-rose-50 text-rose-700',
  hiring_spike: 'bg-sky-50 text-sky-700',
  tech_install: 'bg-purple-50 text-purple-700',
};

const KEIGO_BODY = `件名: TechRise様の Series B 完了、誠におめでとうございます

田中 健太 様

平素より大変お世話になっております。PatronEZ株式会社の佐藤 美咲と申します。

このたび、貴社の Series B ¥1.2B の資金調達完了の報を拝見し、心よりお祝い申し上げます。SoftBank様をはじめとする投資家陣からの大型調達は、貴社の市場でのご躍進を物語るものと存じます。

ところで、急成長フェーズに入られる中、営業組織のシグナル可視化(競合動向、買い手企業の決裁人事、技術スタック変化)が、商機の取りこぼし防止に直結する局面が増えてまいります。弊社 PatronEZ は、御社のような調達直後の成長企業を主な対象に、サンサン代替の人脈+グローバル買い手シグナル統合プラットフォームをご提供しております。

15分ほど、貴社の現在の営業可視化体制について、お話を伺うお時間を頂戴できますでしょうか。来週中であれば、火・水・金の午後にてご都合の合うお時間でお伺いいたします。

何卒よろしくお願い申し上げます。

PatronEZ株式会社
佐藤 美咲
m.sato@patronez.com
+81-3-1234-5678`;

export function DemoPage() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<number | null>(1);
  useDocumentMeta({
    title: 'Live demo — PatronEZ',
    description: 'No-signup PatronEZ demo: real-time funding + job-change + TDnet signals, scored and AI-explained. Runs in 30 seconds.',
    path: '/demo',
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-accent-700 bg-accent-50 rounded-md px-2 py-0.5 mb-2">
              {t('demo.tag')}
            </div>
            <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">
              {t('demo.headline')}
            </h1>
            <p className="mt-1 text-ink-600">{t('demo.intro')}</p>
          </div>
          <Link
            to="/"
            className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition shadow-soft"
          >
            {t('demo.cta')}
          </Link>
        </div>

        <Tile title={t('demo.tile_signals')}>
          <ul className="divide-y divide-ink-100">
            {SIGNALS.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                  className="w-full text-left py-3 px-1 hover:bg-ink-50 flex items-start gap-3"
                >
                  <span
                    className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-2 py-0.5 ${KIND_COLOR[s.kind]}`}
                  >
                    {t(`signals.kind_${s.kind}`)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-900">{s.title}</div>
                    <div className="text-xs text-ink-500">
                      {s.subtitle} · {t('signals.score_label')} <span className="num">{s.score}</span>
                    </div>
                  </div>
                </button>
                {openId === s.id && (
                  <div className="px-1 pb-4">
                    <div className="rounded-xl bg-ink-50 border border-ink-200 p-4 text-sm">
                      <p className="text-xs text-ink-500 mb-2 num">
                        AI · cites signal #{s.id}
                      </p>
                      <p className="text-ink-900">{s.ai}</p>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Tile>

        <Tile title={t('demo.tile_composer')}>
          <pre className="text-sm whitespace-pre-wrap text-ink-900 leading-relaxed font-sans">
            {KEIGO_BODY}
          </pre>
          <p className="mt-3 text-xs text-ink-500 num">{t('demo.ai_meta')}</p>
        </Tile>
      </main>
      <SiteFooter />
    </div>
  );
}

function Tile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-white border border-ink-200 p-5">
      <h2 className="text-xs uppercase tracking-wide font-medium text-ink-500 mb-3">{title}</h2>
      {children}
    </section>
  );
}
